// tslint:disable:unified-signatures
import { Observable, Observer, BehaviorSubject } from 'rxjs';
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';

import { AuthConfig } from '../auth.config';
import { filter, first } from 'rxjs/operators';
import { FeatureFlagService } from './feature-flag.service';

@Injectable()
export class AuthService {
  public session_timer: any;
  public ttl_tiler: any;
  public admin$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
  public logged_in$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _success_path: string | string[];

  constructor(
    private router: Router,
    private http: HttpClient,
    private feature_flag_service: FeatureFlagService,
    @Inject('config') private config: AuthConfig,
  ) {
    this.startClocks();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.restartSessionExpirationClock();
      }
    });
    this.admin$.subscribe((admin) => this.logged_in$.next(!!admin));
    //  Login the user using the token getter
    this.login(this.token)
      .toPromise()
      .catch(() => null);
  }

  public login(token: string): Observable<boolean>;
  public login(username: string, password: string): Observable<boolean>;
  public login(token_or_username: string, password?: string): Observable<boolean> {
    if (password) {
      const username = token_or_username;
      return this.credLogin(username, password);
    } else {
      const token = token_or_username;
      return this.tokenLogin(token);
    }
  }

  public oidcLogin() {
    // NOTE: we cannot relay on 'http' service to visit the OIDC login URL
    // because OIDC auth requires a browser to visit the OIDC login URL rather than
    // a http service to visit the OIDC login URL, which is not possible.
    // 
    // Also, make sure 'this.config.oidc_login_url' is registered in Azure Entra as a valid redirect URL.
    // For example, if the registered Azure Entra redirect URL is 'http://localhost:3300/auth/login'
    // while 'this.config.oidc_login_url' is '127.0.0.1:3300/auth/login',
    // the OIDC login will fail because the registered Azure Entra redirect URL ('http://localhost:3300/auth/login')
    // is different from '127.0.0.1:3300/auth/login',
    // where Azure OIDC auth recognizes these two are different URLs with different sessions
    // although these two URLs are seemingly pointing to the same location.
    window.location.href = this.config.oidc_login_url;
  }

  public get token(): string {
    const token: string = window.sessionStorage.getItem('token');
    return token;
  }

  public get success_path(): string | string[] {
    const REQUESTED_PATH: string =
      this._success_path instanceof Array
        ? this._success_path.filter((path) => path !== '/').join('/')
        : this._success_path || '';
    const DEFAULT_PATH: string =
      this.config.success_path instanceof Array
        ? this.config.success_path.filter((path) => path !== '/').join('/')
        : this.config.success_path || '';
    const AUTH_PATH: string =
      this.config.auth_path instanceof Array
        ? this.config.auth_path.filter((path) => path !== '/').join('/')
        : this.config.auth_path || '';
    const SUCCESS_PATH: string =
      !!REQUESTED_PATH && !REQUESTED_PATH.match(AUTH_PATH) ? REQUESTED_PATH : DEFAULT_PATH;
    delete this._success_path;
    return SUCCESS_PATH;
  }

  public set success_path(path: string | string[]) {
    this._success_path = path;
  }

  public async payload<T>(): Promise<T | undefined> {
    return <T>(<any>await this.admin$
      .asObservable()
      .pipe(
        filter((v) => !!v),
        first(),
      )
      .toPromise());
  }

  public payloadSync<T>(): T | undefined {
    const token = this.token;
    if (!token) {
      return undefined;
    }
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      return undefined;
    }
  }

  private credLogin(username: string, password: string): Observable<boolean> {
    return new Observable<boolean>((observer: Observer<boolean>) => {
      const url: string = this.config.login_url;
      const headers = this.basicAuthHeaders(username, password);
      const responseType = 'text';
      this.http.get(url, { headers, responseType }).subscribe(
        (response: any) => {
          window.sessionStorage.setItem('token', response);
          observer.next(response !== undefined);
          observer.complete();
          this.startClocks();
        },
        (err: HttpErrorResponse) => {
          observer.error(err);
        },
      );
    });
  }

  private tokenLogin(token: string): Observable<boolean> {
    return new Observable<boolean>((observer: Observer<boolean>) => {
      if (!token) {
        // handle untruthy tokens
        observer.error(new Error('No token for token login'));
        return;
      }
      const url: string = this.config.me_url;
      const headers = this.headers(token);
      this.http.get(url, { headers }).subscribe(
        (response: any) => {
          this.admin$.next(response);
          observer.next(response !== undefined);
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          observer.error(err);
        },
      );
    });
  }

  private headers(token): HttpHeaders {
    const headers = new HttpHeaders({ Authorization: `JWT ${token}` });
    return headers;
  }

  private startClocks(): void {
    if (!this.token) {
      return;
    }
    this.startSessionExpirationClock();
    this.startTTLExpirationClock();
  }

  private async startTTLExpirationClock(): Promise<void> {
    try {
      const profile = this.payloadSync<{ issued: number; ttl: number }>();
      if (!profile) {
        throw new Error('Cannot read profile');
      }
      const issued: number = profile.issued;
      const ttl: number = profile.ttl;
      if (!issued) {
        throw new Error('JWT paylod missing "issued" property. Cannot start TTL expiration clock.');
      }
      if (!ttl) {
        throw new Error('JWT paylod missing "ttl" property. Cannot start TTL expiration clock.');
      }
      const time = issued + ttl - new Date().getTime();
      this.ttl_tiler = setTimeout(() => {
        this.logout();
      }, time);
    } catch (e) {
      console.warn(e);
    }
  }

  private startSessionExpirationClock(): void {
    this.stopSessionExpirationClock();
    this.session_timer = setTimeout(() => {
      this.logout();
    }, this.config.timeout);
  }

  private restartSessionExpirationClock(): void {
    this.stopSessionExpirationClock();
    this.startSessionExpirationClock();
  }

  private stopSessionExpirationClock(): void {
    clearTimeout(this.session_timer);
  }

  private stopTtlClock(): void {
    clearTimeout(this.ttl_tiler);
  }

  public logout(): void {
    this.admin$.next(undefined);
    window.sessionStorage.clear();
    this.stopSessionExpirationClock();
    this.stopTtlClock();

    if (this.feature_flag_service.is_oidc_auth_enabled) {
      window.location.href = this.config.oidc_logout_url;
    } else {
      (<any>location).reload();
    }
  }

  public onLoginSuccess(): void {
    this.router.navigateByUrl(<any>this.success_path);
  }

  private basicAuthHeaders(username: string, password: string): HttpHeaders {
    const headers = new HttpHeaders({ Authorization: `Basic ${btoa(`${username}:${password}`)}` });
    return headers;
  }
}
